// Folder picker layer. Uses the File System Access API to let users pick a
// real folder on disk, persists the handle in IndexedDB so subsequent
// visits skip the picker, and surfaces a friendly fallback page on browsers
// that don't support it (Firefox / Safari).
//
// Why IndexedDB and not localStorage: FileSystemHandle objects are not
// serializable to JSON, but ARE structured-cloneable, so they round-trip
// through IndexedDB cleanly. idb-keyval is a 600-byte wrapper around the
// IDB primitive — minimal trust-surface, no real dependency cost.

import { get, set, del } from "idb-keyval";

const HANDLE_KEY = "pf:data-folder-handle";

export function isFolderPickerSupported(): boolean {
  return typeof globalThis !== "undefined" &&
    "showDirectoryPicker" in globalThis;
}

// Prompt the user to pick a folder. The browser shows the OS folder picker;
// the returned handle is what we persist + use for reads/writes.
export async function pickDataFolder(): Promise<FileSystemDirectoryHandle> {
  if (!isFolderPickerSupported()) {
    throw new Error(
      "This browser does not support picking a local folder. Use Chrome or Edge, or download the desktop app.",
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handle = await (globalThis as any).showDirectoryPicker({
    mode: "readwrite",
    id: "personal-finance",
    startIn: "documents",
  });
  await set(HANDLE_KEY, handle);
  return handle;
}

// Try to recover a previously-picked folder. Returns null if there is none
// stored or the user revokes permission. The caller is expected to ask the
// user to re-pick on null.
export async function getStoredFolder(): Promise<FileSystemDirectoryHandle | null> {
  if (!isFolderPickerSupported()) return null;
  const handle = (await get(HANDLE_KEY)) as FileSystemDirectoryHandle | undefined;
  if (!handle) return null;
  // The browser does NOT keep permission across reloads — we have to
  // re-request it. queryPermission returns "granted" only if a still-live
  // tab from this session already has it; on a fresh load we'll always
  // need to call requestPermission, which prompts the user.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const h = handle as any;
  const permission = await h.queryPermission({ mode: "readwrite" });
  if (permission === "granted") return handle;
  const requested = await h.requestPermission({ mode: "readwrite" });
  if (requested === "granted") return handle;
  return null;
}

export async function forgetStoredFolder(): Promise<void> {
  await del(HANDLE_KEY);
}

/** Stable fingerprint for a picked folder: SHA-1 of folder.name + the
 *  alphabetized list of top-level entries, truncated to 16 hex chars.
 *  Used as the per-folder localStorage key for the view filter, so
 *  two different folders with the same name (e.g. ~/A/Finance and
 *  ~/B/Finance) get distinct ids. Same folder picked twice yields the
 *  same id as long as its contents don't drift. */
export async function folderFingerprint(folder: FileSystemDirectoryHandle): Promise<string> {
  const parts: string[] = [folder.name];
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for await (const [name] of (folder as any).entries()) parts.push(name);
  } catch {
    // Entry iteration can fail if permission's been revoked mid-call.
    // Fall back to name-only — collision risk reverts to pre-fix.
  }
  const data = new TextEncoder().encode(parts.sort().join("|"));
  const digest = await globalThis.crypto.subtle.digest("SHA-1", data);
  const bytes = new Uint8Array(digest);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 16);
}

// Read every CSV/PDF file the user has dropped into `raw/<YYYY-MM>/`.
// Mirrors how the desktop pipeline walks the raw directory. Files at
// arbitrary depth are NOT supported — we only walk top-level + the
// raw/<month>/ pattern, same as scripts/process_month.py.
export interface RawFile {
  filename: string;
  bytes: Uint8Array;
  /** Best-effort month inferred from the parent directory name (raw/2026-04/foo.csv → "2026-04"). Empty if not derivable. */
  monthHint: string;
}

export async function readRawFiles(
  folder: FileSystemDirectoryHandle,
): Promise<RawFile[]> {
  const out: RawFile[] = [];
  let rawDir: FileSystemDirectoryHandle | null = null;
  try {
    rawDir = await folder.getDirectoryHandle("raw");
  } catch {
    return out;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for await (const [name, entry] of (rawDir as any).entries()) {
    if (entry.kind !== "directory") continue;
    if (!/^\d{4}-\d{2}$/.test(name)) continue;
    const monthDir = entry as FileSystemDirectoryHandle;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for await (const [fname, fentry] of (monthDir as any).entries()) {
      if (fentry.kind !== "file") continue;
      const f = await (fentry as FileSystemFileHandle).getFile();
      const buf = new Uint8Array(await f.arrayBuffer());
      out.push({ filename: fname, bytes: buf, monthHint: name });
    }
  }
  return out;
}

// Convenience: read a JSON file from the folder (returns null if missing).
export async function readJsonFile<T = unknown>(
  folder: FileSystemDirectoryHandle,
  relativePath: string,
): Promise<T | null> {
  const parts = relativePath.split("/");
  let dir: FileSystemDirectoryHandle = folder;
  for (let i = 0; i < parts.length - 1; i++) {
    try {
      dir = await dir.getDirectoryHandle(parts[i]);
    } catch {
      return null;
    }
  }
  try {
    const fileHandle = await dir.getFileHandle(parts[parts.length - 1]);
    const f = await fileHandle.getFile();
    const text = await f.text();
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

// Write a JSON file, creating any missing parent directories. Atomic via
// the standard write-to-temp-then-rename dance is not directly supported
// by the FS Access API — the createWritable + close sequence is the
// closest equivalent the browser gives us.
export async function writeJsonFile(
  folder: FileSystemDirectoryHandle,
  relativePath: string,
  value: unknown,
): Promise<void> {
  const parts = relativePath.split("/");
  let dir: FileSystemDirectoryHandle = folder;
  for (let i = 0; i < parts.length - 1; i++) {
    dir = await dir.getDirectoryHandle(parts[i], { create: true });
  }
  const fileHandle = await dir.getFileHandle(parts[parts.length - 1], { create: true });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const writable = await (fileHandle as any).createWritable();
  await writable.write(JSON.stringify(value, null, 2));
  await writable.close();
}
